import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { LogIn, Mail, Lock, Loader2 } from 'lucide-react';
import { clientAuthService, LoginClientDto } from '@/services/client-auth.service';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, useToast } from '@caruso/ui';

export function ClientLogin() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState<LoginClientDto>({
    email: '',
    password: '',
  });

  const loginMutation = useMutation({
    mutationFn: (data: LoginClientDto) => clientAuthService.login(data),
    onSuccess: (data) => {
      toast({
        title: 'Login realizado!',
        description: `Bem-vindo de volta, ${data.client.name}!`,
        variant: 'success',
      });
      navigate('/client/dashboard');
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao fazer login',
        description: error.response?.data?.message || 'Email ou senha inválidos',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Preencha email e senha',
        variant: 'destructive',
      });
      return;
    }

    loginMutation.mutate(formData);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center">
                <LogIn className="w-8 h-8 text-brand-gold" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-white">Área do Cliente</CardTitle>
            <CardDescription className="text-neutral-400">
              Faça login para acessar sua conta
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-300">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <Input
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
                    disabled={loginMutation.isPending}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-neutral-300">
                    Senha
                  </label>
                  <Link
                    to="/client/forgot-password"
                    className="text-xs text-brand-gold hover:text-brand-gold/80"
                  >
                    Esqueceu a senha?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <Input
                    type="password"
                    placeholder="Sua senha"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
                    disabled={loginMutation.isPending}
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-brand-gold hover:bg-brand-gold/90 text-black font-semibold"
                disabled={loginMutation.isPending}
              >
                {loginMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    Entrar
                  </>
                )}
              </Button>

              <div className="text-center text-sm text-neutral-400 mt-4">
                Não tem uma conta?{' '}
                <Link
                  to="/client/register"
                  className="text-brand-gold hover:text-brand-gold/80 font-medium"
                >
                  Cadastre-se
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-sm text-neutral-500 hover:text-neutral-400"
          >
            Voltar para o site
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
